import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SSD = () => {
  const teachers = [
    { name: "AMM", limit: 5, subjects: ["IV-GARO", "III-CD/HD", "II-ENG1", "I-Math", "N-ENG", "N-Rhymes"] },
    { name: "BKS", limit: 5, subjects: ["IV-Compp.", "III-Sc.", "II-SSc.", "I-GK", "KG-CD/HD", "KG-ENG"] },
    { name: "KNM", limit: 5, subjects: ["III-GK", "II-GK", "I-ENG", "KG-GARO", "N-HD/PD"] },
    { name: "CRM", limit: 6, subjects: ["VI-Comp.", "V-GARO", "II-Sc.", "II-CD/HD", "I-SSc.", "KG-Sc."] },
    { name: "MDS", limit: 6, subjects: ["IV-HD/CD", "III-ENG1", "I-GARO", "II-GARO", "KG-MSc.", "N-Math"] },
    { name: "RCM", limit: 5, subjects: [ "VIII-H/Ed.", "VII-GARO", "VII-V/Ed.", "VI-H/Ed.", "V-GK", "IV-ENG1"] },
    { name: "TCS", limit: 5, subjects: ["VI-Comp.", "VII-Comp.", "V-EVS", "V-SSc.", "IV-Math", "III-SSc."] },
    { name: "RH", limit: 5, subjects: ["VIII-Hindi", "VII-Hindi", "VI-ENG", "VI-Hindi", "V-Hindi", "IV-ENG2"] },
    { name: "ACM", limit: 5, subjects: ["VII-Math", "VI-Math", "VI-Sc.", "V-Math", "IV-Sc.", "III-Math"] },
    { name: "BMS", limit: 6, subjects: ["X-Sc.", "IX-Sc.", "VIII-Sc.", "VII-GK", "VI-V/Ed.", "V-Sc."] },
    { name: "SM", limit: 4, subjects: ["X-SSc.", "X-H/Ed.", "IX-SSc.", "VIII-ENG", "VIII-EVS", "VI-EVS"] },
    { name: "PKJ", limit: 4, subjects: ["X-Math", "IX-Math", "VIII-Math", "VIII-GK", "VII-Sc."] },
    { name: "HMS", limit: 3, subjects: ["X-GARO", "X-V/Ed.", "IX-Comp.", "IX-V/Ed.", "VIII-GARO"] },
    { name: "YMM", limit: 4, subjects: ["X-EVS", "IX-GARO", "IX-EVS", "VII-EVS", "V-ENG1", "V-Comp."] },
    { name: "NI", limit: 5, subjects: ["X-ENG", "IX-ENG", "IX-HEdu", "VIII-SSc.", "VII-ENG", "VI-GK"] },
    { name: "PWM", limit: 5, subjects: ["VII-SSc.", "VI-Garo", "V-ENG2", "V-MSc.", "IV-SSc.", "III-ENG2"] },
    { name: "LKS", limit: 4, subjects: ["III-GARO", "II-Math", "I-Sc.", "I-CD/HD", "KG-Math", "KG-GK"] },
    { name: "PRS", limit: 6, subjects: ["VIII-V/Ed.", "VII-H/Ed.", "VI-SSc.", "V-H/Ed.", "IV-GK", "II-ENG2"] },
    // Add other teachers with their limits and subjects
  ];

  const classes = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  const periods = 7;

  const initialMatrix = Array.from({ length: classes.length }, () =>
    Array.from({ length: periods }, () => ({ value: "No Period", isDuplicate: false }))
  );

  const [matrix, setMatrix] = useState(initialMatrix);
  const [showModal, setShowModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState({ classIndex: null, periodIndex: null });
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const handleCellClick = (classIndex, periodIndex) => {
    setSelectedCell({ classIndex, periodIndex });
    setShowModal(true);
  };

  const handleTeacherSelect = (teacherName) => {
    const teacher = teachers.find((t) => t.name === teacherName);
    setSelectedTeacher(teacher);
  };

  const handleSubjectSelect = (subject) => {
    const { classIndex, periodIndex } = selectedCell;

    if (classIndex !== null && periodIndex !== null && selectedTeacher) {
      const teacherSubject = `${selectedTeacher.name} (${subject})`;

      const isDuplicate = matrix.some(
        (row, idx) =>
          idx !== classIndex && row[periodIndex]?.value?.startsWith(selectedTeacher.name)
      );

      const updatedMatrix = [...matrix];
      updatedMatrix[classIndex][periodIndex] = { value: teacherSubject, isDuplicate };

      setMatrix(updatedMatrix);
      setShowModal(false);
    }
  };

  const shufflePeriods = () => {
    const shuffledMatrix = matrix.map((row) => {
      const [firstPeriod, ...restPeriods] = row;
      const shuffledRest = [...restPeriods].sort(() => Math.random() - 0.5);
      return [firstPeriod, ...shuffledRest];
    });
    setMatrix(shuffledMatrix);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableData = matrix.map((row, classIndex) => [
      classes[classIndex],
      ...row.map((cell) => cell.value),
    ]);

    doc.text("Timetable", 14, 10);
    doc.autoTable({
      head: [["Class", ...Array.from({ length: periods }, (_, i) => `Period ${i + 1}`)]],
      body: tableData,
    });

    doc.save("timetable.pdf");
  };

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: "20px" }}>
        <Button onClick={shufflePeriods} variant="primary" style={{ marginRight: "10px" }}>
          Shuffle and Save
        </Button>
        <Button onClick={exportToPDF} variant="success">
          Export to PDF
        </Button>
      </div>
      <table border="1" style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th>Class</th>
            {[...Array(periods)].map((_, index) => (
              <th key={index}>Period {index + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, classIndex) => (
            <tr key={classIndex}>
              <td>{classes[classIndex]}</td>
              {row.map((cell, periodIndex) => (
                <td
                  key={periodIndex}
                  onClick={() => handleCellClick(classIndex, periodIndex)}
                  style={{
                    cursor: "pointer",
                    background: cell.isDuplicate ? "red" : cell.value === "No Period" ? "#f0f0f0" : "#dff0d8",
                  }}
                >
                  {cell.value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Subject</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Teacher</Form.Label>
            <Form.Control as="select" onChange={(e) => handleTeacherSelect(e.target.value)}>
              <option value="">Select a teacher</option>
              {teachers.map((teacher) => (
                <option key={teacher.name} value={teacher.name}>
                  {teacher.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          {selectedTeacher && (
            <Form.Group style={{ marginTop: "20px" }}>
              <Form.Label>Select Subject</Form.Label>
              <Form.Control as="select" onChange={(e) => handleSubjectSelect(e.target.value)}>
                <option value="">Select a subject</option>
                {selectedTeacher.subjects.map((subject, index) => (
                  <option key={index} value={subject}>
                    {subject}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SSD;
